import { t as uiText } from '../i18n';
// src/components/OGASetting.tsx
import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import DelayedTooltip from './DelayedTooltip';
import { getHtmlPagesEnabled } from '../api/publicPages';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import { applyContactSeed, type ContactPathSeed } from '../features/onboarding/contactPathHints';
import './OGASetting.css';

interface OGASettingProps {
  workspaceId?: string;
  boardId?: string;
  title?: string;
  variant?: 'page' | 'panel';
  seed?: ContactPathSeed;
  onSaved?: () => void | Promise<void>;
}

const OGASetting: React.FC<OGASettingProps> = ({
  workspaceId,
  boardId,
  title,
  variant = 'page',
  seed,
  onSaved,
}) => {
  const { can } = useWorkspaceEntitlements();
  const [settings, setSettings] = useState({
    showAnswerPage: false,
    showInlineSourceUrls: false,
    crawlReferencedUrls: false,
    showContactHeading: false,
    showContactUrl: false,
    showContactPhone: false,
    showCustomLink: false
  });

  const [contactHeadingSettings, setContactHeadingSettings] = useState({
    displayText: uiText("Contact information")
  });

  const [contactUrlSettings, setContactUrlSettings] = useState({
    url: '',
    displayText: '',
    displayLabel: 'Web',
    displayTitle: ''
  });

  const [contactPhoneSettings, setContactPhoneSettings] = useState({
    phoneNumber: '',
    displayText: '',
    displayLabel: uiText("Phone")
  });

  const [customLinkSettings, setCustomLinkSettings] = useState({
    url: '',
    displayText: '',
    displayLabel: uiText("Other"),
    displayTitle: ''
  });

  const [contactSectionOrder, setContactSectionOrder] = useState<string[]>(['phone', 'url', 'customLink']);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [htmlPagesEnabled, setHtmlPagesEnabled] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const phoneRef = useRef(contactPhoneSettings);
  phoneRef.current = contactPhoneSettings;
  const urlRef = useRef(contactUrlSettings);
  urlRef.current = contactUrlSettings;
  const isPanel = variant === 'panel';

  // 設定を読み込む関数
  const loadSettings = async () => {
    if (!workspaceId) return;
    
    setLoading(true);
    setLoaded(false);
    try {
      const [settingsSnap, htmlEnabled] = await Promise.all([
        getDoc(doc(db, 'publicPages', workspaceId, 'settings', 'OGA')),
        getHtmlPagesEnabled(workspaceId).catch(() => false),
      ]);
      setHtmlPagesEnabled(htmlEnabled === true);
      const data = settingsSnap.exists() ? settingsSnap.data() : undefined;
      let crawlReferencedUrls = false;
      if (typeof data?.crawlReferencedUrls === 'boolean') {
        crawlReferencedUrls = data.crawlReferencedUrls;
      } else {
        const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
        crawlReferencedUrls = wsSnap.exists() && wsSnap.data()?.crawlReferencedUrls === true;
      }

      const seeded = applyContactSeed({
        showContactPhone: data?.showContactPhone === true,
        phoneNumber: typeof data?.contactPhoneSettings?.phoneNumber === 'string'
          ? data.contactPhoneSettings.phoneNumber
          : '',
        showContactUrl: data?.showContactUrl === true,
        contactUrl: typeof data?.contactUrlSettings?.url === 'string'
          ? data.contactUrlSettings.url
          : '',
      }, seedRef.current ?? {});

      if (settingsSnap.exists() && data) {
        setSettings({
          showAnswerPage: htmlEnabled === true && (data.showAnswerPage ?? false),
          showInlineSourceUrls: data.showInlineSourceUrls ?? false,
          crawlReferencedUrls,
          showContactHeading: data.showContactHeading ?? false,
          showContactUrl: seeded.showContactUrl,
          showContactPhone: seeded.showContactPhone,
          showCustomLink: data.showCustomLink ?? false
        });
        setContactHeadingSettings({
          displayText: data.contactHeadingSettings?.displayText ?? uiText("Contact information")
        });
        setContactUrlSettings({
          url: seeded.contactUrl,
          displayText: data.contactUrlSettings?.displayText ?? '',
          displayLabel: data.contactUrlSettings?.displayLabel ?? 'Web',
          displayTitle: data.contactUrlSettings?.displayTitle ?? ''
        });
        setContactPhoneSettings({
          phoneNumber: seeded.phoneNumber,
          displayText: data.contactPhoneSettings?.displayText ?? '',
          displayLabel: data.contactPhoneSettings?.displayLabel ?? uiText("Phone")
        });
        setCustomLinkSettings({
          url: data.customLinkSettings?.url ?? '',
          displayText: data.customLinkSettings?.displayText ?? '',
          displayLabel: data.customLinkSettings?.displayLabel ?? uiText("Other"),
          displayTitle: data.customLinkSettings?.displayTitle ?? ''
        });
        if (Array.isArray(data.contactSectionOrder) && data.contactSectionOrder.length > 0) {
          setContactSectionOrder(data.contactSectionOrder);
        }
      } else {
        setSettings({
          showAnswerPage: false,
          showInlineSourceUrls: false,
          crawlReferencedUrls,
          showContactHeading: false,
          showContactUrl: seeded.showContactUrl,
          showContactPhone: seeded.showContactPhone,
          showCustomLink: false
        });
        setContactUrlSettings(prev => ({ ...prev, url: seeded.contactUrl }));
        setContactPhoneSettings(prev => ({ ...prev, phoneNumber: seeded.phoneNumber }));
      }
      setLoaded(true);
    } catch (error) {
      console.error('設定の読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  // 設定を保存する関数
  const saveSettings = async () => {
    if (!workspaceId) {
      alert(uiText("Workspace ID not found"));
      return;
    }
    
    // バリデーション
    const validationErrors: string[] = [];
    
    // 問い合わせページリンクがONの場合
    if (settings.showContactUrl) {
      if (!contactUrlSettings.displayTitle.trim()) {
        validationErrors.push(uiText("Inquiry page link display title is not entered"));
      }
      if (!contactUrlSettings.url.trim()) {
        validationErrors.push(uiText("Inquiry page link URL is not entered"));
      }
    }
    
    // 問い合わせ電話番号がONの場合
    if (settings.showContactPhone) {
      if (!contactPhoneSettings.phoneNumber.trim()) {
        validationErrors.push(uiText("No phone number entered"));
      }
    }
    
    // 任意リンクがONの場合
    if (settings.showCustomLink) {
      if (!customLinkSettings.displayTitle.trim()) {
        validationErrors.push(uiText("Custom link title is empty"));
      }
      if (!customLinkSettings.url.trim()) {
        validationErrors.push(uiText("Custom link URL is empty"));
      }
    }
    
    // バリデーションエラーがある場合は保存を中止
    if (validationErrors.length > 0) {
      alert(uiText("Validation error:\n{{v0}}", { v0: validationErrors.join('\n') }));
      return;
    }
    
    setSaving(true);
    try {
      const ogaDocRef = doc(db, 'publicPages', workspaceId, 'settings', 'OGA');
      const settingsData = {
        showAnswerPage: htmlPagesEnabled === true && settings.showAnswerPage,
        showInlineSourceUrls: settings.showInlineSourceUrls,
        crawlReferencedUrls: settings.crawlReferencedUrls,
        showContactHeading: settings.showContactHeading,
        showContactUrl: settings.showContactUrl,
        showContactPhone: settings.showContactPhone,
        showCustomLink: settings.showCustomLink,
        contactHeadingSettings: {
          displayText: contactHeadingSettings.displayText
        },
        contactUrlSettings: {
          url: contactUrlSettings.url,
          displayText: contactUrlSettings.displayText,
          displayLabel: contactUrlSettings.displayLabel,
          displayTitle: contactUrlSettings.displayTitle
        },
        contactPhoneSettings: {
          phoneNumber: contactPhoneSettings.phoneNumber,
          displayText: contactPhoneSettings.displayText,
          displayLabel: contactPhoneSettings.displayLabel
        },
        customLinkSettings: {
          url: customLinkSettings.url,
          displayText: customLinkSettings.displayText,
          displayLabel: customLinkSettings.displayLabel,
          displayTitle: customLinkSettings.displayTitle
        },
        contactSectionOrder,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(ogaDocRef, settingsData);
      if (isPanel) {
        await onSaved?.();
      } else {
        alert(uiText("Settings saved successfully"));
      }
    } catch (error) {
      console.error('設定の保存エラー:', error);
      alert(uiText("Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  // コンポーネントマウント時に設定を読み込む
  useEffect(() => {
    loadSettings();
  }, [workspaceId]);

  const seedKey = `${seed?.phoneNumber ?? ''}\n${seed?.inquiryUrl ?? ''}`;
  useEffect(() => {
    if (!loaded) return;
    const applied = applyContactSeed({
      showContactPhone: settingsRef.current.showContactPhone,
      phoneNumber: phoneRef.current.phoneNumber,
      showContactUrl: settingsRef.current.showContactUrl,
      contactUrl: urlRef.current.url,
    }, seedRef.current ?? {});
    if (applied.phoneNumber !== phoneRef.current.phoneNumber) {
      setContactPhoneSettings(prev => ({ ...prev, phoneNumber: applied.phoneNumber }));
    }
    if (applied.contactUrl !== urlRef.current.url) {
      setContactUrlSettings(prev => ({ ...prev, url: applied.contactUrl }));
    }
    if (
      applied.showContactPhone !== settingsRef.current.showContactPhone
      || applied.showContactUrl !== settingsRef.current.showContactUrl
    ) {
      setSettings(prev => ({
        ...prev,
        showContactPhone: applied.showContactPhone,
        showContactUrl: applied.showContactUrl,
      }));
    }
  }, [loaded, seedKey]);

  const handleSettingChange = (key: keyof typeof settings, value: boolean) => {
    if (key === 'showAnswerPage' && htmlPagesEnabled !== true && value) return;
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const moveSectionOrder = (key: string, direction: 'up' | 'down') => {
    setContactSectionOrder(prev => {
      const idx = prev.indexOf(key);
      if (idx < 0) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
  };

  return (
    <div
      className={isPanel ? 'oga-setting oga-setting--panel' : 'oga-setting'}
      style={isPanel ? {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        color: '#6b7280',
      } : {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        padding: '20px',
        backgroundColor: '#ffffff',
        color: '#6b7280',
        width: '100%',
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}
    >
      <div
        className="oga-setting__card"
        style={isPanel ? {
          width: '100%',
          boxSizing: 'border-box',
        } : {
          width: '100%',
          maxWidth: '800px',
          backgroundColor: '#ffffff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        {isPanel ? (
          loading ? (
            <p style={{ fontSize: 'var(--oga-body-size, 14px)', color: '#6c757d', margin: '0 0 12px' }}>
              {uiText("(Loading...)")}
            </p>
          ) : null
        ) : (
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#495057',
          marginBottom: '24px',
          borderBottom: '2px solid var(--workspace-primary, #cfae91)',
          paddingBottom: '8px'
        }}>
          {uiText("OGA settings")}
          {loading && (
            <span style={{
              fontSize: 'var(--oga-body-size, 14px)',
              fontWeight: 'normal',
              color: '#6c757d',
              marginLeft: '8px'
            }}>
              {uiText("(Loading...)")}
            </span>
          )}
        </h2>
        )}

        {/* 設定項目1: 回答ページを提示する */}
        {can('oga.answerPage') && (
        <div style={{
          marginBottom: '24px',
          padding: 'var(--oga-card-pad, 16px)',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="oga-setting__toggle-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: 'var(--oga-title-size, 16px)',
              fontWeight: '500',
              color: '#495057',
              cursor: htmlPagesEnabled === true ? 'pointer' : 'default'
            }}>
              {uiText("Suggest answer pages")}
            </label>
            <DelayedTooltip
              placement="top"
              width={280}
              disabled={htmlPagesEnabled !== false}
              content={uiText("Can't show answer pages because Web publishing is off. Turn on \"Publish page on the web\" in public page settings to use this.")}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: 'var(--oga-body-size, 14px)',
                  color: settings.showAnswerPage ? '#28a745' : '#6c757d',
                  fontWeight: '500'
                }}>
                  {settings.showAnswerPage ? 'ON' : 'OFF'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (htmlPagesEnabled !== true) return;
                    handleSettingChange('showAnswerPage', !settings.showAnswerPage);
                  }}
                  disabled={htmlPagesEnabled !== true}
                  aria-label={uiText("Suggest answer pages")}
                  style={{
                    width: '48px',
                    height: '24px',
                    backgroundColor: settings.showAnswerPage ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: htmlPagesEnabled === true ? 'pointer' : 'not-allowed',
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    opacity: htmlPagesEnabled === true ? 1 : 0.7
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: settings.showAnswerPage ? '26px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>
            </DelayedTooltip>
          </div>
          <p style={{
            fontSize: 'var(--oga-body-size, 14px)',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.4'
          }}>
            {uiText("Choose whether to show answer pages to the user")}
          </p>
        </div>
        )}

        {/* 設定項目: ページ本文内のリンクも提示する */}
        <div style={{
          marginBottom: '24px',
          padding: 'var(--oga-card-pad, 16px)',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="oga-setting__toggle-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: 'var(--oga-title-size, 16px)',
              fontWeight: '500',
              color: '#495057',
              cursor: 'pointer'
            }}>
              {uiText("Also show links in the page body")}
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: settings.showInlineSourceUrls ? '#28a745' : '#6c757d',
                fontWeight: '500'
              }}>
                {settings.showInlineSourceUrls ? 'ON' : 'OFF'}
              </span>
              <button
                onClick={() => handleSettingChange('showInlineSourceUrls', !settings.showInlineSourceUrls)}
                style={{
                  width: '48px',
                  height: '24px',
                  backgroundColor: settings.showInlineSourceUrls ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: settings.showInlineSourceUrls ? '26px' : '2px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          </div>
          <p style={{
            fontSize: 'var(--oga-body-size, 14px)',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.4'
          }}>
            {uiText("URLs written directly in the page body (such as links to external services) are also shown in the answer when highly relevant (this works independently of the setting that shows answer pages. Links inside crawled pages are excluded)")}
          </p>
        </div>

        {/* 設定項目: 参照リンク先を読み取って補足回答する */}
        {can('oga.crawlReferencedUrls') && (
        <div style={{
          marginBottom: '24px',
          padding: 'var(--oga-card-pad, 16px)',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="oga-setting__toggle-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: 'var(--oga-title-size, 16px)',
              fontWeight: '500',
              color: '#495057',
              cursor: 'pointer'
            }}>
              {uiText("Read referenced links and add a follow-up answer")}
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: settings.crawlReferencedUrls ? '#28a745' : '#6c757d',
                fontWeight: '500'
              }}>
                {settings.crawlReferencedUrls ? 'ON' : 'OFF'}
              </span>
              <button
                onClick={() => handleSettingChange('crawlReferencedUrls', !settings.crawlReferencedUrls)}
                style={{
                  width: '48px',
                  height: '24px',
                  backgroundColor: settings.crawlReferencedUrls ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: settings.crawlReferencedUrls ? '26px' : '2px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          </div>
          <p style={{
            fontSize: 'var(--oga-body-size, 14px)',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.4'
          }}>
            {uiText("Fetches the content of external URLs included in public RAG answers and adds a follow-up after the first answer")}
          </p>
        </div>
        )}

        <hr style={{
          border: 'none',
          borderTop: '1px solid #e9ecef',
          margin: '0 0 16px'
        }} />

        <p style={{
          fontSize: 'var(--oga-body-size, 14px)',
          color: '#6c757d',
          margin: '0 0 24px',
          lineHeight: '1.4'
        }}>
          {uiText("If a question comes in a different language than the one set here, AI translates this text automatically.")}
        </p>

        {/* 設定項目: 任意テキストの表示（連絡先見出し） */}
        {can('oga.contactHeading') && (
        <div style={{
          marginBottom: '24px',
          padding: 'var(--oga-card-pad, 16px)',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="oga-setting__toggle-row" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label style={{
              fontSize: 'var(--oga-title-size, 16px)',
              fontWeight: '500',
              color: '#495057',
              cursor: 'pointer'
            }}>
              {uiText("Header text display")}
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: settings.showContactHeading ? '#28a745' : '#6c757d',
                fontWeight: '500'
              }}>
                {settings.showContactHeading ? 'ON' : 'OFF'}
              </span>
              <button
                onClick={() => handleSettingChange('showContactHeading', !settings.showContactHeading)}
                style={{
                  width: '48px',
                  height: '24px',
                  backgroundColor: settings.showContactHeading ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: settings.showContactHeading ? '26px' : '2px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          </div>
          <p style={{
            fontSize: 'var(--oga-body-size, 14px)',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.4'
          }}>
            {uiText("Show optional text above the contact information")}
          </p>
          
          {settings.showContactHeading && (
            <div style={{
              marginTop: '16px',
              padding: 'var(--oga-card-pad, 16px)',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{
                fontSize: 'var(--oga-body-size, 14px)',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '12px'
              }}>
                {uiText("Display text settings")}
              </h4>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#495057',
                  marginBottom: '4px'
                }}>
                  {uiText("Text")}
                </label>
                <input
                  type="text"
                  value={contactHeadingSettings.displayText}
                  onChange={(e) => setContactHeadingSettings(prev => ({
                    ...prev,
                    displayText: e.target.value
                  }))}
                  placeholder={uiText("Contact information")}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'var(--oga-body-size, 14px)',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#f1f3f5',
                borderRadius: '4px',
                border: '1px dashed #ced4da'
              }}>
                <p style={{
                  fontSize: '12px',
                  color: '#868e96',
                  margin: '0 0 4px',
                  fontWeight: '500'
                }}>
                  {uiText("Display preview")}
                </p>
                <div style={{
                  fontSize: '13px',
                  color: '#495057',
                  lineHeight: '1.6'
                }}>
                  {contactHeadingSettings.displayText || uiText("Contact information")}
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* 連絡先セクション（順序に従って動的レンダリング） */}
        {contactSectionOrder.map((sectionKey, sectionIdx) => {
          if (sectionKey === 'url') return (
            <div key="url" style={{
              marginBottom: '24px',
              padding: 'var(--oga-card-pad, 16px)',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <div className="oga-setting__toggle-row" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{
                  fontSize: 'var(--oga-title-size, 16px)',
                  fontWeight: '500',
                  color: '#495057',
                  cursor: 'pointer'
                }}>
                  {uiText("Show inquiry page link")}
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => moveSectionOrder('url', 'up')}
                    disabled={sectionIdx === 0}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === 0 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === 0 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▲</button>
                  <button
                    onClick={() => moveSectionOrder('url', 'down')}
                    disabled={sectionIdx === contactSectionOrder.length - 1}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === contactSectionOrder.length - 1 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === contactSectionOrder.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === contactSectionOrder.length - 1 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▼</button>
                  <span style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    color: settings.showContactUrl ? '#28a745' : '#6c757d',
                    fontWeight: '500'
                  }}>
                    {settings.showContactUrl ? 'ON' : 'OFF'}
                  </span>
                  <button
                    onClick={() => handleSettingChange('showContactUrl', !settings.showContactUrl)}
                    style={{
                      width: '48px',
                      height: '24px',
                      backgroundColor: settings.showContactUrl ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: settings.showContactUrl ? '26px' : '2px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              </div>
              <p style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: '#6c757d',
                margin: '0',
                lineHeight: '1.4'
              }}>
                {uiText("Choose whether to show an inquiry page link")}
              </p>
              {settings.showContactUrl && (
                <div style={{
                  marginTop: '16px',
                  padding: 'var(--oga-card-pad, 16px)',
                  backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '12px'
                  }}>
                    {uiText("Inquiry page link settings")}
                  </h4>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Guidance text")}</label>
                    <textarea
                      value={contactUrlSettings.displayText}
                      onChange={(e) => setContactUrlSettings(prev => ({ ...prev, displayText: e.target.value }))}
                      placeholder={uiText("Contact us")}
                      rows={3}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Item name")}</label>
                    <input
                      type="text"
                      value={contactUrlSettings.displayLabel}
                      onChange={(e) => setContactUrlSettings(prev => ({ ...prev, displayLabel: e.target.value }))}
                      placeholder="Web"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Display title")}</label>
                    <input
                      type="text"
                      value={contactUrlSettings.displayTitle}
                      onChange={(e) => setContactUrlSettings(prev => ({ ...prev, displayTitle: e.target.value }))}
                      placeholder={uiText("Contact")}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>URL</label>
                    <input
                      type="url"
                      value={contactUrlSettings.url}
                      onChange={(e) => setContactUrlSettings(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com/contact"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f1f3f5', borderRadius: '4px', border: '1px dashed #ced4da' }}>
                    <p style={{ fontSize: '12px', color: '#868e96', margin: '0 0 4px', fontWeight: '500' }}>{uiText("Display preview")}</p>
                    <div style={{ fontSize: '13px', color: '#495057', lineHeight: '1.6' }}>
                      {contactUrlSettings.displayText?.trim() && (<div>{contactUrlSettings.displayText}</div>)}
                      <div>{contactUrlSettings.displayLabel?.trim() ? `${contactUrlSettings.displayLabel}: ` : ''}<span style={{ color: '#1971c2', textDecoration: 'underline' }}>{contactUrlSettings.displayTitle || uiText("Contact")}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

          if (sectionKey === 'phone') return (
            <div key="phone" style={{
              marginBottom: '24px',
              padding: 'var(--oga-card-pad, 16px)',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <div className="oga-setting__toggle-row" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{
                  fontSize: 'var(--oga-title-size, 16px)',
                  fontWeight: '500',
                  color: '#495057',
                  cursor: 'pointer'
                }}>
                  {uiText("Phone number display")}
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => moveSectionOrder('phone', 'up')}
                    disabled={sectionIdx === 0}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === 0 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === 0 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▲</button>
                  <button
                    onClick={() => moveSectionOrder('phone', 'down')}
                    disabled={sectionIdx === contactSectionOrder.length - 1}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === contactSectionOrder.length - 1 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === contactSectionOrder.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === contactSectionOrder.length - 1 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▼</button>
                  <span style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    color: settings.showContactPhone ? '#28a745' : '#6c757d',
                    fontWeight: '500'
                  }}>
                    {settings.showContactPhone ? 'ON' : 'OFF'}
                  </span>
                  <button
                    onClick={() => handleSettingChange('showContactPhone', !settings.showContactPhone)}
                    style={{
                      width: '48px',
                      height: '24px',
                      backgroundColor: settings.showContactPhone ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: settings.showContactPhone ? '26px' : '2px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              </div>
              <p style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: '#6c757d',
                margin: '0',
                lineHeight: '1.4'
              }}>
                {uiText("Show a phone number in the contact details")}
              </p>
              {settings.showContactPhone && (
                <div style={{
                  marginTop: '16px',
                  padding: 'var(--oga-card-pad, 16px)',
                  backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '12px'
                  }}>
                    {uiText("Phone number display settings")}
                  </h4>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Guidance text")}</label>
                    <textarea
                      value={contactPhoneSettings.displayText}
                      onChange={(e) => setContactPhoneSettings(prev => ({ ...prev, displayText: e.target.value }))}
                      placeholder={uiText("Call us")}
                      rows={3}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Item name")}</label>
                    <input
                      type="text"
                      value={contactPhoneSettings.displayLabel}
                      onChange={(e) => setContactPhoneSettings(prev => ({ ...prev, displayLabel: e.target.value }))}
                      placeholder={uiText("Phone")}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Phone number")}</label>
                    <input
                      type="tel"
                      value={contactPhoneSettings.phoneNumber}
                      onChange={(e) => setContactPhoneSettings(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      placeholder="03-1234-5678"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f1f3f5', borderRadius: '4px', border: '1px dashed #ced4da' }}>
                    <p style={{ fontSize: '12px', color: '#868e96', margin: '0 0 4px', fontWeight: '500' }}>{uiText("Display preview")}</p>
                    <div style={{ fontSize: '13px', color: '#495057', lineHeight: '1.6' }}>
                      {contactPhoneSettings.displayText?.trim() && (<div>{contactPhoneSettings.displayText}</div>)}
                      <div>{contactPhoneSettings.displayLabel?.trim() ? `${contactPhoneSettings.displayLabel}: ` : ''}{contactPhoneSettings.phoneNumber || '03-1234-5678'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

          if (sectionKey === 'customLink' && !can('oga.customLink')) return null;
          if (sectionKey === 'customLink') return (
            <div key="customLink" style={{
              marginBottom: '24px',
              padding: 'var(--oga-card-pad, 16px)',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <div className="oga-setting__toggle-row" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{
                  fontSize: 'var(--oga-title-size, 16px)',
                  fontWeight: '500',
                  color: '#495057',
                  cursor: 'pointer'
                }}>
                  {uiText("Show custom link")}
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => moveSectionOrder('customLink', 'up')}
                    disabled={sectionIdx === 0}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === 0 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === 0 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▲</button>
                  <button
                    onClick={() => moveSectionOrder('customLink', 'down')}
                    disabled={sectionIdx === contactSectionOrder.length - 1}
                    style={{
                      width: '26px', height: '26px', border: '1px solid #ced4da', borderRadius: '4px',
                      backgroundColor: sectionIdx === contactSectionOrder.length - 1 ? '#f8f9fa' : '#ffffff',
                      cursor: sectionIdx === contactSectionOrder.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '11px', color: sectionIdx === contactSectionOrder.length - 1 ? '#ced4da' : '#495057',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >▼</button>
                  <span style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    color: settings.showCustomLink ? '#28a745' : '#6c757d',
                    fontWeight: '500'
                  }}>
                    {settings.showCustomLink ? 'ON' : 'OFF'}
                  </span>
                  <button
                    onClick={() => handleSettingChange('showCustomLink', !settings.showCustomLink)}
                    style={{
                      width: '48px',
                      height: '24px',
                      backgroundColor: settings.showCustomLink ? 'var(--workspace-primary, #cfae91)' : '#6c757d',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: settings.showCustomLink ? '26px' : '2px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              </div>
              <p style={{
                fontSize: 'var(--oga-body-size, 14px)',
                color: '#6c757d',
                margin: '0',
                lineHeight: '1.4'
              }}>
                {uiText("Choose whether to show a custom link")}
              </p>
              {settings.showCustomLink && (
                <div style={{
                  marginTop: '16px',
                  padding: 'var(--oga-card-pad, 16px)',
                  backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{
                    fontSize: 'var(--oga-body-size, 14px)',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '12px'
                  }}>
                    {uiText("Custom link settings")}
                  </h4>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Guidance text")}</label>
                    <textarea
                      value={customLinkSettings.displayText}
                      onChange={(e) => setCustomLinkSettings(prev => ({ ...prev, displayText: e.target.value }))}
                      placeholder={uiText("See the details")}
                      rows={3}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Item name")}</label>
                    <input
                      type="text"
                      value={customLinkSettings.displayLabel}
                      onChange={(e) => setCustomLinkSettings(prev => ({ ...prev, displayLabel: e.target.value }))}
                      placeholder={uiText("Other")}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>{uiText("Display title")}</label>
                    <input
                      type="text"
                      value={customLinkSettings.displayTitle}
                      onChange={(e) => setCustomLinkSettings(prev => ({ ...prev, displayTitle: e.target.value }))}
                      placeholder={uiText("Detailed information")}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>URL</label>
                    <input
                      type="url"
                      value={customLinkSettings.url}
                      onChange={(e) => setCustomLinkSettings(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com/custom-link"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: 'var(--oga-body-size, 14px)', backgroundColor: '#ffffff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#f1f3f5', borderRadius: '4px', border: '1px dashed #ced4da' }}>
                    <p style={{ fontSize: '12px', color: '#868e96', margin: '0 0 4px', fontWeight: '500' }}>{uiText("Display preview")}</p>
                    <div style={{ fontSize: '13px', color: '#495057', lineHeight: '1.6' }}>
                      {customLinkSettings.displayText?.trim() && (<div>{customLinkSettings.displayText}</div>)}
                      <div>{customLinkSettings.displayLabel?.trim() ? `${customLinkSettings.displayLabel}: ` : ''}<span style={{ color: '#1971c2', textDecoration: 'underline' }}>{customLinkSettings.displayTitle || uiText("Detailed information")}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

          return null;
        })}

        {/* 保存ボタン */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '32px'
        }}>
          <button
            onClick={saveSettings}
            disabled={saving || loading}
            style={{
              backgroundColor: saving || loading ? '#6c757d' : 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              fontSize: 'var(--oga-title-size, 16px)',
              fontWeight: '500',
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (!saving && !loading) {
                e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
              }
            }}
            onMouseOut={(e) => {
              if (!saving && !loading) {
                e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
              }
            }}
          >
            {saving ? uiText("Saving...") : uiText("Save settings")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OGASetting;
